import {
	Button,
	ColorV2,
	Menu,
	ProgressBar,
} from "@adoptaunabuelo/react-components";
import { Auth, CoorporateScheme, UserScheme } from "client-v2";
import { ArrowLeft, CircleUser, Power } from "lucide-react";
import React, {
	CSSProperties,
	forwardRef,
	Ref,
	useEffect,
	useImperativeHandle,
	useState,
} from "react";
import styled, { keyframes } from "styled-components";
import media from "styled-media-query";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";

import Personal from "../section/login/Personal";
import Code from "../section/login/Code";
import Login from "../section/login/Login";
import { useNavigate } from "react-router";
import { ConfirmationResult, getAuth } from "firebase/auth";

const navBarHeight = 72;
const marginBottom = 16;
const paddingTop = 40;
const paddingBottom = 24;
const navBarHeightMobile = 58;
const marginBottomMobile = 0;
const paddingTopMobile = 24;
const paddingBottomMobile = 0;
const MainContainer = styled.div`
	display: flex;
	align-items: center;
	height: var(--doc-height);
	width: calc(100vw - 160px);
	padding: 0px 80px;
	overflow: hidden;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	${media.lessThan("medium")`
		width: 100vw;
		padding: 0px;
	`}
`;
const NavBar = styled.div<{ $hideBackButton?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	height: ${navBarHeight}px;
	min-height: ${navBarHeight}px;
	width: 100%;
	max-width: 1200px;
	justify-content: center;
	${media.lessThan("medium")`
		width: calc(100% - 16px);
		justify-content: flex-end;
        padding: 0px 8px;
		height: ${navBarHeightMobile}px;
		min-height: ${navBarHeightMobile}px;
    `}
`;
const NavBarRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
`;
const DataContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	width: 100%;
	max-width: 1200px;
	margin-bottom: ${marginBottom}px;
	${media.lessThan("medium")`
		margin-bottom: ${marginBottomMobile}px;
	`}
`;
const LeftContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	max-width: 50%;
	${media.lessThan("medium")`
		max-width: 100%;
    `}
`;
const RightContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	max-width: 50%;
	${media.lessThan("medium")`
		max-width: 100%;
    `}
`;
const CentralContainer = styled.div`
	position: relative;
	display: flex;
	flex: 1;
	align-items: center;
	padding: ${paddingTop}px 32px ${paddingBottom}px;
	border-radius: 24px;
	flex-direction: column;
	background-color: white;
	overflow: hidden;
	min-height: calc(
		var(--doc-height) - ${navBarHeight}px - ${marginBottom}px -
			${paddingTop}px - ${paddingBottom}px
	);
	max-height: calc(
		var(--doc-height) - ${navBarHeight}px - ${marginBottom}px -
			${paddingTop}px - ${paddingBottom}px
	);
	overflow: scroll;
	${media.lessThan("medium")`
		padding: ${paddingTopMobile}px 24px ${paddingBottomMobile}px;
		min-height: calc(var(--doc-height) - ${navBarHeightMobile}px - ${marginBottomMobile}px - ${paddingTopMobile}px - ${paddingBottomMobile}px);
		max-height: calc(var(--doc-height) - ${navBarHeightMobile}px - ${marginBottomMobile}px  - ${paddingTopMobile}px - ${paddingBottomMobile}px);
	`}
`;
const CentralContainerView = styled.div<{
	$animation: string;
	$animationTime: number;
}>`
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
	width: 420px;
	max-width: 100%;
	height: 100%;
	animation-name: ${(props) =>
		props.$animation === "fadeIn" ? fadeIn : fadeOut};
	animation-duration: ${(props) => props.$animationTime}s;
	animation-timing-function: ease-in;
	${media.lessThan("medium")`
		width: 100%;
    `}
`;
const NavigationLeftView = styled.div`
	display: flex;
	align-items: center;
`;
const NavigationCentralView = styled.div`
	display: flex;
	flex: 1;
	height: 100%;
	align-items: center;
	padding: 0px 16px;
	${media.lessThan("medium")`
		padding: 0px 8px;
    `}
`;
const NavigationRightView = styled.div`
	display: flex;
	justify-content: flex-end;
`;
const MenuView = styled.div``;

// Animación de entrada
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
	opacity: 1;
    transform: translateY(0);
  }
`;

// Animación de salida
const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0px);
  }
  to {
	opacity: 0;
    transform: translateY(20px);
  }
`;

const WizardContainer = forwardRef(
	(props: WizardContainerProps, ref: Ref<WizardContainerRef>) => {
		const navigate = useNavigate();

		const [secondsRemaining, setSecondsRemaining] = useState(0);
		const [step, setStep] = useState(0);
		const [views, setViews] = useState<ViewsProps[]>([]);
		const [user, setUser] = useState<UserScheme | undefined>(undefined);
		const [loginInfo, setLoginInfo] = useState<
			| { confirmationResult: ConfirmationResult; phone: string }
			| undefined
		>(undefined);
		// const [loginInfo, setLoginInfo] = useState<UserScheme | undefined>(
		// 	undefined
		// );
		const [animation, setAnimation] = useState<string>("fadeIn");
		const animationTime = 0.6;

		const dispatch = useDispatch();
		const { setShowError, setCurrentUser } = bindActionCreators(
			actionsCreators,
			dispatch
		);
		const currentUser = useSelector(
			(state: State) => state.user.currentUser
		);
		const isMobile: boolean = useSelector(
			(state: State) => state.screen.isMobile
		);
		const loginViews = [
			//Login phone
			{
				hideBackButton: true,
				leftView: <></>,
				centralView: (
					<Login
						corporate={props.corporate}
						onFinish={(result) => {
							if (result.error) {
								setShowError({
									show: true,
									message: result.error,
								});
							} else if (result.data) {
								setLoginInfo(result.data);
								setSecondsRemaining(60);
								handleNextStep();
							}
						}}
					/>
				),
				rightView: <></>,
			},
			//SMS code
			{
				leftView: <></>,
				centralView: loginInfo ? (
					<Code
						confirmationResult={loginInfo.confirmationResult}
						phone={loginInfo.phone}
						// User={loginInfo}
						countdown={secondsRemaining}
						onFinish={(result) => {
							if (result.error) {
								setShowError({
									show: true,
									message: result.error,
								});
							} else if (result.data) {
								setUser(result.data);
								setCurrentUser(result.data);
								props.onLogin && props.onLogin(result.data);
								const hasData = checkUserData(result.data);
								if (hasData) handleNextStep(2);
								else handleNextStep();
							}
						}}
					/>
				) : undefined,
				rightView: <></>,
				hideBackButton: secondsRemaining <= 0 ? false : true,
			},
			//Personal data
			props.personalView
				? props.personalView
				: {
						leftView: <></>,
						centralView: user ? (
							<Personal
								user={user}
								corporate={props.corporate}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setUser(result.data);
										setCurrentUser(result.data);
										handleNextStep();
									}
								}}
							/>
						) : null,
						rightView: <></>,
				  },
		];

		useEffect(() => {
			setViews([...loginViews, ...props.views]);
		}, [props.views, user, secondsRemaining]);

		useEffect(() => {
			if (secondsRemaining > 0) {
				const timer = setInterval(() => {
					setSecondsRemaining((prev) => prev - 1);
				}, 1000);
				return () => clearInterval(timer);
			}
		}, [secondsRemaining]);

		useEffect(() => {
			if (currentUser) {
				//Check if user has all the data
				setUser(currentUser);
				const hasData = checkUserData(currentUser);
				if (hasData) {
					setStep(3);
				} else setStep(2);
			}
			return () => {
				setLoginInfo(undefined);
			};
		}, []);

		useImperativeHandle(ref, () => ({
			goBack(steps?: number, currentStep?: number) {
				return onBackClick(steps, currentStep);
			},
			goNext(steps?: number) {
				return handleNextStep(steps);
			},
			setStep(step: number) {
				setStep(step);
				return step;
			},
		}));

		const checkUserData = (user: UserScheme) => {
			if (user.name && user.surname && user.email) {
				if (props.personalView?.birthdayRequired) {
					return user.birthday ? true : false;
				} else return true;
			} else return false;
		};

		const handleNextStep = (steps?: number) => {
			setAnimation("fadeOut");
			setTimeout(() => {
				onNextClick(steps);
				setAnimation("fadeIn");
			}, animationTime * 1000);
		};

		const handleBackStep = (steps?: number) => {
			if (step === 0 || step === 3) {
				navigate(-1);
			} else {
				setAnimation("fadeOut");
				setTimeout(() => {
					onBackClick(steps);
					setAnimation("fadeIn");
				}, animationTime * 1000);
			}
		};

		const onBackClick = (steps?: number, currentStep?: number) => {
			const newStep =
				(currentStep ? currentStep : step) - (steps ? steps : 1);
			if (newStep >= 0) {
				setStep(newStep);
				props.onBackClick && props.onBackClick(newStep);
				return newStep;
			} else return 0;
		};

		const onNextClick = (steps?: number) => {
			const newStep = step + (steps ? steps : 1);
			if (newStep <= views.length || newStep <= loginViews.length) {
				setStep(newStep);
				return newStep;
			} else return step;
		};

		const onOptionClick = async (option: any) => {
			if (option.id === "logout") {
				const auth = getAuth();
				await Auth.logout({ auth: auth });
				window.location.reload();
			}
		};

		return props.loading ? (
			<></>
		) : views.length > 0 ? (
			<MainContainer>
				<NavBar $hideBackButton={views[step].hideBackButton}>
					{!views[step].hideNavBar && (
						<>
							<NavBarRow>
								{!views[step].hideBackButton && (
									<Button
										design="image"
										icon={<ArrowLeft />}
										onClick={() => handleBackStep()}
									/>
								)}
								<NavigationLeftView>
									{views[step].navigationLeftView}
								</NavigationLeftView>
								<NavigationCentralView>
									{!isMobile && step + 1 < views.length && (
										<ProgressBar
											style={{
												backgroundColor:
													"rgba(0, 29, 61, 0.10)",
												height: 4,
												width: "100%",
											}}
											maxValue={views.length}
											progress={[
												{
													value: step + 1,
													color: props.corporate
														?.color?.main,
												},
											]}
										/>
									)}
								</NavigationCentralView>
								<NavigationRightView>
									{views[step].navigationRightView}
									{currentUser && (
										<MenuView>
											<Menu
												id={"header-menu-list"}
												icon={
													<CircleUser
														color={
															ColorV2.text
																.neutralHard
														}
													/>
												}
												menuStyle={{
													width: "max-content",
												}}
												options={[
													{
														id: "logout",
														label: "Cerrar sesión",
														icon: <Power />,
													},
												]}
												position={"bottom-left"}
												onClick={onOptionClick}
											/>
										</MenuView>
									)}
								</NavigationRightView>
							</NavBarRow>
							{isMobile && step + 1 < views.length && (
								<NavBarRow>
									<ProgressBar
										style={{
											backgroundColor: "#001D3D1A",
											height: 4,
											margin: "0px 12px 6px",
											width: "100%",
										}}
										maxValue={views.length}
										progress={[
											{
												value: step + 1,
												color: props.corporate?.color
													?.main,
											},
										]}
									/>
								</NavBarRow>
							)}
						</>
					)}
				</NavBar>
				<DataContainer style={views[step].containerStyle}>
					{views[step].leftView && (
						<LeftContainer
							style={{
								...views[step].leftViewStyle,
							}}
						>
							{views[step].leftView}
						</LeftContainer>
					)}
					{views[step].centralView && (
						<CentralContainer
							style={views[step].centralContainerStyle}
						>
							<CentralContainerView
								style={views[step].centralViewStyle}
								$animation={animation}
								$animationTime={animationTime}
							>
								{views[step].centralView}
							</CentralContainerView>
						</CentralContainer>
					)}
					{views[step].rightView && (
						<RightContainer
							style={{
								...views[step].rightViewStyle,
							}}
						>
							{views[step].rightView}
						</RightContainer>
					)}
				</DataContainer>
			</MainContainer>
		) : null;
	}
);
export default WizardContainer;
export interface WizardContainerProps {
	loading?: boolean;
	views: ViewsProps[];
	personalView?: ViewsProps & {
		birthdayRequired?: boolean;
	};
	corporate?: CoorporateScheme;
	onBackClick?: (step: number) => void;
	onLogin?: (user: UserScheme) => void;
}
interface ViewsProps {
	containerStyle?: CSSProperties;
	leftViewStyle?: CSSProperties;
	leftView?: React.ReactNode;
	centralViewStyle?: CSSProperties;
	centralContainerStyle?: CSSProperties;
	centralView?: React.ReactNode;
	rightViewStyle?: CSSProperties;
	rightView?: React.ReactNode;
	navigationLeftView?: React.ReactNode;
	navigationRightView?: React.ReactNode;
	hideBackButton?: boolean;
	hideNavBar?: boolean;
}
export interface WizardContainerRef {
	goBack: (steps?: number, currentStep?: number) => number;
	goNext: (steps?: number) => void;
	setStep: (step: number) => number;
}
